import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Utensils, ArrowRight, Check, Minus, ChevronDown, Wrench, Sparkles, TrendingUp, PieChart, ShieldCheck, Handshake, Brain, ChefHat, Bot, Puzzle, Flame, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AffiliateStrip } from '../components/AffiliateStrip';
import SEO from '../components/SEO';
import { HeroSlideshow, KX_HERO_SLIDES } from '../components/HeroSlideshow';
import { ShareEarnBar } from '../components/ShareEarnBar';
import HowWeWork from '../components/HowWeWork';
import ScanPromo from '../components/ScanPromo';
import { fetchPaymentOptions, startStripeCheckout, startPaypalCheckout, stripeLinkWithRef } from '../lib/checkout';
import { trackEvent } from '../lib/analytics';
import { useT } from '../i18n';

const T = (en, es) => ({ en, es });

const SCOPE_NOTE = T(
  'Final pricing depends on project scope, number of locations and business requirements.',
  'El precio final depende del alcance del proyecto, número de ubicaciones y necesidades del negocio.'
);

const FLAGSHIP_PLANS = [
  {
    id: 'indexai',
    icon: Search,
    name: 'Index AI™',
    category: T('AI Search Visibility Platform', 'Plataforma de Visibilidad en Búsqueda IA'),
    step: T('01 · Where most restaurants start', '01 · Donde empieza la mayoría'),
    tagline: T('Become Visible in AI Search', 'Hazte Visible en la Búsqueda con IA'),
    desc: T(
      'Help ChatGPT, Gemini, Google AI and other AI engines recommend your restaurant.',
      'Haz que ChatGPT, Gemini, Google AI y otros motores de IA recomienden tu restaurante.'
    ),
    price: 99,
    ideal: T(
      ['Independent Restaurants', 'Food Trucks', 'Multi-location Restaurants'],
      ['Restaurantes Independientes', 'Food Trucks', 'Restaurantes Multi-local']
    ),
    included: T(
      ['Initial AI Visibility Audit', 'Full AI Search Setup', 'AI Index Optimization', 'AI Search Monitoring', 'Monthly Optimization', 'Monthly Performance Report', 'AI Visibility Recommendations', 'Email Support'],
      ['Auditoría inicial de visibilidad IA', 'Configuración completa de búsqueda IA', 'Optimización del índice IA', 'Monitoreo de búsqueda IA', 'Optimización mensual', 'Reporte mensual de rendimiento', 'Recomendaciones de visibilidad IA', 'Soporte por email']
    ),
    cta: T('Start with Index AI', 'Empieza con Index AI'),
    highlight: false,
  },
  {
    id: 'workforce',
    icon: Users,
    name: 'AI Workforce™',
    category: T('Restaurant AI Employees', 'Empleados IA para Restaurantes'),
    step: T('02 · The natural next step', '02 · El siguiente paso natural'),
    tagline: T('Your AI Team Working 24/7', 'Tu Equipo de IA Trabajando 24/7'),
    desc: T(
      'Deploy AI specialists to support your restaurant without hiring additional staff.',
      'Despliega especialistas de IA para tu restaurante sin contratar más personal.'
    ),
    price: 199,
    ideal: T(
      ['Growing Restaurants', 'Hospitality Groups', 'Busy Owners'],
      ['Restaurantes en Crecimiento', 'Grupos de Hostelería', 'Dueños sin Tiempo']
    ),
    included: T(
      ['AI Team Deployment', 'Rachel — Customer Experience', 'Penny — Editorial Director', 'Sonny — Content Distribution', 'Stan — Growth & Lead Generation', 'Eva — Executive Operations', 'Monthly Optimization', 'Workflow Improvements', 'Performance Review', 'AI Prompt Updates', 'Priority Support'],
      ['Despliegue del equipo IA', 'Rachel — Experiencia de Cliente', 'Penny — Directora Editorial', 'Sonny — Distribución de Contenido', 'Stan — Crecimiento y Leads', 'Eva — Operaciones Ejecutivas', 'Optimización mensual', 'Mejora de flujos de trabajo', 'Revisión de rendimiento', 'Actualización de prompts', 'Soporte prioritario']
    ),
    cta: T('Build My AI Team', 'Crea Mi Equipo de IA'),
    highlight: true,
  },
  {
    id: 'catering',
    icon: Utensils,
    name: 'Catering AI™',
    category: T('Corporate Catering Platform', 'Plataforma de Catering Corporativo'),
    step: T('03 · Grow into the full platform', '03 · Crece a la plataforma completa'),
    tagline: T('The Complete AI-Powered Catering Platform', 'La Plataforma Completa de Catering con IA'),
    desc: T(
      'Launch, manage and grow your catering business with ecommerce, AI visibility and AI automation.',
      'Lanza, gestiona y haz crecer tu negocio de catering con ecommerce, visibilidad IA y automatización IA.'
    ),
    price: 399,
    ideal: T(
      ['Restaurants launching catering', 'Corporate Catering', 'Multi-location Catering'],
      ['Restaurantes lanzando catering', 'Catering Corporativo', 'Catering Multi-local']
    ),
    included: T(
      ['Catering Ecommerce Platform', 'Online Ordering', 'Secure Payments', 'Menu Configuration', 'Customer Database', 'AI Visibility (Index AI™)', 'AI Workforce™ included', 'Monthly Optimization', 'Monthly Strategy Meeting', 'Performance Reports', 'Continuous Improvements'],
      ['Plataforma ecommerce de catering', 'Pedidos online', 'Pagos seguros', 'Configuración de menús', 'Base de datos de clientes', 'Visibilidad IA (Index AI™)', 'AI Workforce™ incluido', 'Optimización mensual', 'Reunión mensual de estrategia', 'Reportes de rendimiento', 'Mejoras continuas']
    ),
    cta: T('Launch Catering AI', 'Lanza Catering AI'),
    highlight: false,
    finalizing: true,
  },
];

const COMPARE = [
  { f: T('Initial Setup', 'Configuración inicial'), v: [1, 1, 1] },
  { f: T('Monthly Optimization', 'Optimización mensual'), v: [1, 1, 1] },
  { f: T('AI Search Visibility', 'Visibilidad en búsqueda IA'), v: [1, 1, 1] },
  { f: T('AI Team', 'Equipo de IA'), v: [0, 1, 1] },
  { f: T('Catering Ecommerce', 'Ecommerce de catering'), v: [0, 0, 1] },
  { f: T('Monthly Reports', 'Reportes mensuales'), v: [1, 1, 1] },
  { f: T('Strategy Reviews', 'Revisiones de estrategia'), v: [T('Quarterly', 'Trimestral'), T('Monthly', 'Mensual'), T('Monthly', 'Mensual')] },
  { f: T('Priority Support', 'Soporte prioritario'), v: [0, 1, 1] },
];

const PILLARS = [
  { icon: ShieldCheck, t: T('Managed, not DIY', 'Gestionado, no DIY'), b: T('We configure, monitor and optimize everything. You run your restaurant — we run your technology.', 'Configuramos, monitoreamos y optimizamos todo. Tú diriges tu restaurante — nosotros tu tecnología.') },
  { icon: Handshake, t: T('One Technology Partner', 'Un solo socio tecnológico'), b: T('One team, one strategy, one point of contact — instead of six disconnected vendors.', 'Un equipo, una estrategia, un punto de contacto — en lugar de seis proveedores desconectados.') },
  { icon: Brain, t: T('Human Experts + AI', 'Expertos humanos + IA'), b: T('AI does the heavy lifting. Real specialists review, decide and improve — every month.', 'La IA hace el trabajo pesado. Especialistas reales revisan, deciden y mejoran — cada mes.') },
  { icon: ChefHat, t: T('Built exclusively for Restaurants', 'Creado exclusivamente para restaurantes'), b: T('Every workflow, report and integration is designed for how restaurants actually operate.', 'Cada flujo, reporte e integración está diseñado para cómo operan los restaurantes en realidad.') },
];

const CHOOSER = [
  { need: T('“I want AI to recommend my restaurant.”', '“Quiero que la IA recomiende mi restaurante.”'), id: 'indexai', name: 'Index AI™', href: '/index-ai' },
  { need: T('“I need more Google visibility.”', '“Necesito más visibilidad en Google.”'), id: 'indexai', name: 'Index AI™', href: '/index-ai' },
  { need: T('“I need an AI receptionist.”', '“Necesito un recepcionista con IA.”'), id: 'workforce', name: 'AI Workforce™', href: '/ai-agents-for-restaurants' },
  { need: T('“I need clear reporting.”', '“Necesito reportes claros.”'), id: 'workforce', name: 'AI Workforce™', href: '/ai-agents-for-restaurants' },
  { need: T('“I need POS support.”', '“Necesito soporte de POS.”'), id: 'workforce', name: T('IT & POS Systems', 'IT y Sistemas POS'), href: '/it-pos-systems' },
  { need: T('“I want to grow catering.”', '“Quiero hacer crecer mi catering.”'), id: 'catering', name: 'Catering AI™', href: '/ai/catering-ai' },
];

const MODULES = [
  {
    icon: Wrench,
    name: T('Restaurant Technology', 'Tecnología de Restaurante'),
    href: '/it-pos-systems',
    headline: T('Keep your restaurant running smoothly.', 'Mantén tu restaurante funcionando sin fricciones.'),
    para: T(
      'Perfect for restaurants that need reliable daily technology support.',
      'Perfecto para restaurantes que necesitan soporte tecnológico diario y confiable.'
    ),
    caps: T(
      ['POS Management', 'Menu Updates', 'Remote Support', 'Hardware Assistance', 'System Monitoring'],
      ['Gestión de POS', 'Actualización de menús', 'Soporte remoto', 'Asistencia de hardware', 'Monitoreo de sistemas']
    ),
    price: 99,
  },
  {
    icon: Sparkles,
    name: T('Digital Branding', 'Branding Digital'),
    href: '/digital-branding',
    headline: T('Help more customers discover your restaurant.', 'Haz que más clientes descubran tu restaurante.'),
    para: T(
      'Everything needed to improve visibility and online presence.',
      'Todo lo necesario para mejorar tu visibilidad y presencia online.'
    ),
    caps: T(
      ['Website Optimization', 'Google Business Profile', 'Local SEO', 'Content Management', 'Reputation Support'],
      ['Optimización del sitio web', 'Perfil de Google Business', 'SEO local', 'Gestión de contenido', 'Soporte de reputación']
    ),
    price: 199,
  },
  {
    icon: TrendingUp,
    name: T('Revenue Growth', 'Crecimiento de Ingresos'),
    href: '/restaurant-revenue-growth',
    headline: T('Turn more visitors into paying customers.', 'Convierte más visitantes en clientes que pagan.'),
    para: T(
      'Strategies designed to increase sales without increasing workload.',
      'Estrategias diseñadas para aumentar ventas sin aumentar tu carga de trabajo.'
    ),
    caps: T(
      ['Menu Engineering', 'Delivery Optimization', 'Promotions', 'Pricing Strategy', 'Conversion Optimization'],
      ['Ingeniería de menús', 'Optimización de delivery', 'Promociones', 'Estrategia de precios', 'Optimización de conversión']
    ),
    price: 199,
  },
  {
    icon: PieChart,
    name: T('Data & Analytics', 'Datos y Analítica'),
    href: '/services/data-analytics',
    headline: T('See your business clearly, every week.', 'Ve tu negocio con claridad, cada semana.'),
    para: T(
      'Dashboards and reporting that turn scattered data into confident decisions.',
      'Dashboards y reportes que convierten datos dispersos en decisiones seguras.'
    ),
    caps: T(
      ['Live Dashboards', 'Performance Reporting', 'Business Intelligence', 'Weekly Insights'],
      ['Dashboards en vivo', 'Reportes de rendimiento', 'Business intelligence', 'Insights semanales']
    ),
    price: 99,
  },
  {
    icon: Bot,
    name: T('AI & Automation', 'IA y Automatización'),
    href: '/ai-solutions',
    headline: T('Put AI to work across your operation.', 'Pon la IA a trabajar en toda tu operación.'),
    para: T(
      'Intelligent automations that handle repetitive work 24/7 — so your team doesn’t have to.',
      'Automatizaciones inteligentes que resuelven el trabajo repetitivo 24/7 — para que tu equipo no tenga que hacerlo.'
    ),
    caps: T(
      ['AI Receptionist', 'Review Automation', 'Custom AI Workflows', 'Automated Follow-ups'],
      ['Recepcionista IA', 'Automatización de reseñas', 'Flujos de IA a medida', 'Seguimientos automáticos']
    ),
    price: 199,
  },
  {
    icon: Puzzle,
    name: T('Custom Solutions', 'Soluciones a Medida'),
    href: '/contact?type=discovery',
    headline: T('Built around your exact needs.', 'Construido alrededor de tus necesidades exactas.'),
    para: T(
      'For groups, chains and operations that need something truly unique.',
      'Para grupos, cadenas y operaciones que necesitan algo verdaderamente único.'
    ),
    caps: T(
      ['Custom Integrations', 'Multi-location Rollouts', 'Dedicated Strategy'],
      ['Integraciones a medida', 'Despliegues multi-local', 'Estrategia dedicada']
    ),
    price: null,
  },
];

const FAQS = [
  {
    q: T('Are these software subscriptions?', '¿Son suscripciones de software?'),
    a: T('No. Every platform is a managed service: KITXENS configures, monitors and continuously optimizes it for you. You never manage tools.', 'No. Cada plataforma es un servicio gestionado: KITXENS lo configura, monitorea y optimiza continuamente por ti. Nunca gestionas herramientas.'),
  },
  {
    q: T('Do I need technical knowledge?', '¿Necesito conocimientos técnicos?'),
    a: T('None. You receive results and reports — we handle everything technical behind the scenes.', 'Ninguno. Tú recibes resultados y reportes — nosotros nos encargamos de todo lo técnico.'),
  },
  {
    q: T('Are there long-term contracts?', '¿Hay contratos a largo plazo?'),
    a: T('No. All platforms are month-to-month. We earn your renewal with results, not clauses.', 'No. Todas las plataformas son mes a mes. Nos ganamos tu renovación con resultados, no con cláusulas.'),
  },
  {
    q: T('How do we start?', '¿Cómo empezamos?'),
    a: T('Your way: a short discovery call, an email or WhatsApp. Then a free audit, expert setup and monthly optimization.', 'A tu manera: una llamada corta de discovery, un email o WhatsApp. Luego una auditoría gratis, setup experto y optimización mensual.'),
  },
  {
    q: T('Can I combine platforms?', '¿Puedo combinar plataformas?'),
    a: T('Yes. Many restaurants start with Index AI™ and add AI Workforce™. Catering AI™ already includes both.', 'Sí. Muchos restaurantes empiezan con Index AI™ y añaden AI Workforce™. Catering AI™ ya incluye ambas.'),
  },
];

function PlanCard({ p, isEs, pay }) {
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState(null); // 'stripe' | 'paypal' | null
  const Icon = p.icon;
  const pick = (t) => (isEs ? t.es : t.en);
  const payLink = pay?.links?.[p.id] || null;
  const buy = async (method) => {
    setBuying(method);
    trackEvent('stripe_checkout_started', { plan: p.id, method });
    try {
      if (method === 'stripe') await startStripeCheckout(p.id);
      else await startPaypalCheckout(p.id);
    } catch {
      setBuying(null);
    }
  };
  return (
    <div className={`relative flex flex-col rounded-3xl border bg-white p-8 transition-all ${p.highlight ? 'border-[#F77F11] shadow-[0_40px_90px_-40px_rgba(247,127,17,0.5)]' : 'border-neutral-200 hover:border-[#F77F11]/60'}`} data-testid={`plan-${p.id}`}>
      {p.highlight && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#F77F11] text-white text-[11px] font-bold uppercase tracking-wide whitespace-nowrap">
          {isEs ? 'Más popular' : 'Most popular'}
        </span>
      )}
      {p.finalizing && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-neutral-950 text-[#ffb374] text-[11px] font-bold uppercase tracking-wide whitespace-nowrap">
          {isEs ? 'Lanzamiento final · Reserva tu lugar' : 'Being finalized · Reserve your spot'}
        </span>
      )}
      <div className="mx-auto w-12 h-12 rounded-2xl bg-neutral-950 text-white flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="mt-5 text-center text-2xl font-bold text-neutral-900">{p.name}</h3>
      <p className="mt-1 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400">{pick(p.category)}</p>
      <p className="text-center text-[15px] font-semibold text-[#F77F11] mt-2">{pick(p.tagline)}</p>
      <p className="mt-3 text-center text-[14px] text-neutral-600 leading-relaxed">{pick(p.desc)}</p>

      <div className="mt-6 text-center">
        <span className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-500 text-[11px] font-semibold px-3 py-1 mb-3" data-testid={`plan-step-${p.id}`}>
          {pick(p.step)}
        </span>
        <p className="text-[12px] text-neutral-500">{isEs ? 'Desde' : 'Starting from'}</p>
        <p className="text-4xl font-bold text-neutral-900">${p.price}<span className="text-base font-medium text-neutral-500">/{isEs ? 'mes' : 'month'}</span></p>
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#F77F11]" data-testid={`plan-founding-${p.id}`}>
          <Flame className="w-3.5 h-3.5" />
          {isEs ? 'Precio de lanzamiento — tu tarifa se congela al contratar' : 'Founding price — your rate locks in when you join'}
        </p>
        <p className="mt-2 text-[12px] text-neutral-400 leading-relaxed">{pick(SCOPE_NOTE)}</p>
      </div>

      <div className="mt-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">{isEs ? 'Ideal para' : 'Ideal for'}</p>
        <ul className="space-y-1.5">
          {pick(p.ideal).map((x) => (
            <li key={x} className="flex items-center gap-2 text-[13.5px] text-neutral-700">
              <Check className="w-4 h-4 text-[#F77F11] shrink-0" /> {x}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-5 flex items-center justify-between w-full text-left text-[13.5px] font-semibold text-neutral-900 border-t border-neutral-100 pt-4"
        data-testid={`plan-toggle-${p.id}`}
      >
        {isEs ? 'Qué incluye exactamente' : 'What exactly is included'}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="mt-3 space-y-1.5" data-testid={`plan-included-${p.id}`}>
          {pick(p.included).map((x) => (
            <li key={x} className="flex items-start gap-2 text-[13px] text-neutral-600">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> <span className="min-w-0">{x}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-7 flex flex-col gap-2.5">
        {payLink ? (
          <Button asChild className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 rounded-full font-semibold w-full">
            <a href={stripeLinkWithRef(payLink)} target="_blank" rel="noopener noreferrer" data-testid={`plan-buy-${p.id}`}>
              <CreditCard className="w-4 h-4 mr-1.5" /> {isEs ? `Contratar ahora — $${p.price}/mes` : `Buy now — $${p.price}/mo`}
            </a>
          </Button>
        ) : pay?.native ? (
          <Button onClick={() => buy('stripe')} disabled={!!buying} className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 rounded-full font-semibold w-full" data-testid={`plan-buy-${p.id}`}>
            {buying === 'stripe'
              ? (isEs ? 'Abriendo pago seguro…' : 'Opening secure checkout…')
              : <><CreditCard className="w-4 h-4 mr-1.5" /> {isEs ? `Contratar ahora — $${p.price}/mes` : `Buy now — $${p.price}/mo`}</>}
          </Button>
        ) : (
          <Button asChild className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 rounded-full font-semibold w-full">
            <Link to={`/contact?platform=${p.id}`} data-testid={`plan-cta-${p.id}`}>
              {pick(p.cta)} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        )}
        {pay?.paypal && (
          <Button onClick={() => buy('paypal')} disabled={!!buying} className="bg-[#FFC439] hover:bg-[#f2b636] text-[#111827] h-12 rounded-full font-bold w-full" data-testid={`plan-paypal-${p.id}`}>
            {buying === 'paypal'
              ? (isEs ? 'Abriendo PayPal…' : 'Opening PayPal…')
              : <><span className="italic font-extrabold tracking-tight"><span className="text-[#003087]">Pay</span><span className="text-[#0070E0]">Pal</span></span>&nbsp;— ${p.price}/{isEs ? 'mes' : 'mo'}</>}
          </Button>
        )}
        <Button asChild variant="outline" className="border-neutral-300 text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 h-11 rounded-full font-medium w-full">
          <Link to={`/contact?platform=${p.id}&type=discovery`} data-testid={`plan-discovery-${p.id}`}>
            {isEs ? 'Agendar Discovery Call gratis' : 'Book a Free Discovery Call'}
          </Link>
        </Button>
        <p className="text-center text-[11.5px] text-neutral-400">
          {isEs ? 'Sin permanencia · cancela cuando quieras · resultados desde el primer mes' : 'No lock-in · cancel anytime · results from month one'}
        </p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { lang } = useT();
  const [pay, setPay] = useState(null);
  useEffect(() => {
    fetchPaymentOptions()
      .then((d) => setPay({ native: !!d.native_checkout, paypal: !!d.paypal, links: { indexai: d.indexai, workforce: d.workforce, catering: d.catering } }))
      .catch(() => {});
  }, []);
  const isEs = lang === 'es';
  const pick = (t) => (isEs ? t.es : t.en);

  return (
    <div data-testid="pricing-page">
      <SEO
        title={isEs ? 'Precios | Kitxens — Plataformas gestionadas para restaurantes' : 'Pricing | Kitxens — Managed platforms for restaurants'}
        description={isEs ? 'Precios simples. Crecimiento poderoso. Index AI™ desde $99/mes, AI Workforce™ desde $199/mes, Catering AI™ desde $399/mes.' : 'Simple pricing. Powerful restaurant growth. Index AI™ from $99/mo, AI Workforce™ from $199/mo, Catering AI™ from $399/mo.'}
        path="/pricing"
      />

      {/* Hero — cinematic slideshow, left-aligned */}
      <section className="relative bg-neutral-950 text-white overflow-hidden pt-28 md:pt-32" data-testid="pricing-hero">
        <HeroSlideshow slides={KX_HERO_SLIDES} overlay="bg-gradient-to-r from-black/85 via-black/60 to-black/40" testId="pricing-hero-slideshow" />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 pt-10 pb-14 sm:py-20 lg:py-24">
          <p className="kx-eyebrow mb-5">{isEs ? 'PRECIOS' : 'PRICING'}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05] max-w-3xl drop-shadow-lg">
            {isEs ? 'Precios Simples.' : 'Simple Pricing.'}<br />
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {isEs ? 'Crecimiento Poderoso.' : 'Powerful Restaurant Growth.'}
            </span>
          </h1>
          <p className="mt-6 text-neutral-100 text-base sm:text-lg max-w-2xl leading-relaxed drop-shadow">
            {isEs
              ? 'Elige la plataforma gestionada que mejor encaje con tu restaurante hoy. Escala cuando estés listo.'
              : 'Choose the managed platform that best fits your restaurant today. Scale whenever you’re ready.'}
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13.5px] text-neutral-200">
            {(isEs
              ? ['Sin contratos a largo plazo', 'Precios transparentes', 'Onboarding experto', 'Optimización continua']
              : ['No long-term contracts', 'Transparent pricing', 'Expert onboarding', 'Continuous optimization']
            ).map((x) => (
              <span key={x} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#F77F11]" /> {x}</span>
            ))}
          </div>
          <Button asChild className="mt-9 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-8 rounded-full text-[15px] font-semibold">
            <Link to="/contact?type=consultation" data-testid="pricing-hero-consult">
              {isEs ? 'Agendar Consulta Gratis' : 'Book a Free Consultation'} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Section 1 — Flagship Platforms */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-orange-50/40 to-white" data-testid="pricing-flagships">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'NUESTRAS PLATAFORMAS' : 'OUR FLAGSHIP PLATFORMS'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Tres plataformas. Un solo objetivo: tu crecimiento.' : 'Three platforms. One goal: your growth.'}
            </h2>
          </div>
          {/* Growth path — subtle value ladder */}
          <div className="mb-12 flex flex-col items-center gap-3" data-testid="pricing-growth-path">
            <p className="text-[13px] font-semibold text-neutral-500">
              {isEs ? 'Empieza pequeño. Escala con KITXENS.' : 'Start small. Scale with KITXENS.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[13.5px]">
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-1.5 font-semibold text-neutral-800">Index AI™ <span className="text-neutral-400 font-medium">{isEs ? 'desde $99/mes' : 'from $99/mo'}</span></span>
              <ArrowRight className="w-4 h-4 text-[#F77F11]" />
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-1.5 font-semibold text-neutral-800">AI Workforce™ <span className="text-neutral-400 font-medium">{isEs ? 'desde $199/mes' : 'from $199/mo'}</span></span>
              <ArrowRight className="w-4 h-4 text-[#F77F11]" />
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-1.5 font-semibold text-neutral-800">Catering AI™ <span className="text-neutral-400 font-medium">{isEs ? 'desde $399/mes' : 'from $399/mo'}</span></span>
            </div>
            <p className="text-[12px] text-neutral-400 max-w-md text-center">
              {isEs
                ? 'Así crece la mayoría de restaurantes con nosotros — a su propio ritmo.'
                : 'This is how most restaurants grow with us — at their own pace.'}
            </p>
          </div>
          {/* Urgency — founding pricing */}
          <div className="mb-8 mx-auto max-w-2xl rounded-2xl border border-[#F77F11]/30 bg-gradient-to-r from-orange-50 to-amber-50/60 px-5 py-4 flex items-center justify-center gap-3" data-testid="pricing-urgency-banner">
            <Flame className="w-5 h-5 text-[#F77F11] shrink-0" />
            <p className="text-[13.5px] text-neutral-800 leading-snug">
              <span className="font-bold">{isEs ? 'Precios de lanzamiento.' : 'Founding pricing.'}</span>{' '}
              {isEs
                ? 'Estas tarifas subirán al cerrar la fase de lanzamiento — quien contrata hoy conserva su precio para siempre.'
                : 'These rates go up once our launch window closes — join today and keep your price forever.'}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {FLAGSHIP_PLANS.map((p) => <PlanCard key={p.id} p={p} isEs={isEs} pay={pay} />)}
          </div>
        </div>
      </section>

      {/* Section 2 — Compare */}
      <section className="py-14 lg:py-20 bg-white" data-testid="pricing-compare">
        <div className="max-w-[980px] mx-auto px-6 lg:px-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight text-center mb-8">
            {isEs ? 'Compara las Plataformas' : 'Compare Platforms'}
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-neutral-200">
            <table className="w-full text-[13.5px] min-w-[560px]">
              <thead>
                <tr className="bg-neutral-950 text-white">
                  <th className="text-left px-5 py-3.5 font-semibold">{isEs ? 'Característica' : 'Feature'}</th>
                  <th className="px-4 py-3.5 font-semibold">Index AI™</th>
                  <th className="px-4 py-3.5 font-semibold">AI Workforce™</th>
                  <th className="px-4 py-3.5 font-semibold">Catering AI™</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={i} className={i % 2 ? 'bg-orange-50/30' : 'bg-white'}>
                    <td className="px-5 py-3 text-neutral-700 font-medium">{pick(row.f)}</td>
                    {row.v.map((v, j) => (
                      <td key={j} className="px-4 py-3 text-center">
                        {v === 1 ? <Check className="w-4 h-4 text-[#F77F11] inline" /> : v === 0 ? <Minus className="w-4 h-4 text-neutral-300 inline" /> : <span className="text-neutral-600">{pick(v)}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 3 — Why Kitxens */}
      <section className="py-16 lg:py-20 bg-neutral-950 text-white" data-testid="pricing-why">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <p className="kx-eyebrow mb-4">{isEs ? '¿POR QUÉ KITXENS?' : 'WHY KITXENS?'}</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            {isEs ? 'No vendemos software.' : 'We don’t sell software.'}{' '}
            <span className="text-[#ffb374]">{isEs ? 'Gestionamos tecnología.' : 'We manage technology.'}</span>
          </h2>
          <p className="mt-6 text-neutral-300 leading-relaxed text-[15.5px] max-w-2xl mx-auto">
            {isEs
              ? 'A diferencia de los vendedores de software, Kitxens no te da simplemente acceso a herramientas. Las configuramos. Las optimizamos. Las monitoreamos. Las mejoramos continuamente.'
              : 'Unlike software vendors, KITXENS doesn’t simply give you access to tools. We configure them. We optimize them. We monitor them. We continuously improve them.'}
          </p>
          <p className="mt-4 text-white font-semibold text-lg">
            {isEs
              ? 'Tu restaurante recibe un departamento tecnológico completo — no otra suscripción mensual.'
              : 'Your restaurant receives an entire technology department instead of another monthly subscription.'}
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            {PILLARS.map(({ icon: Icon, t, b }) => (
              <div key={t.en} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid={`why-pillar-${t.en.split(' ')[0].toLowerCase().replace(',', '')}`}>
                <span className="inline-flex w-10 h-10 rounded-xl bg-[#F77F11]/15 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#F77F11]" />
                </span>
                <h3 className="mt-4 font-bold text-[15.5px] text-white">{pick(t)}</h3>
                <p className="mt-2 text-[13.5px] text-neutral-300 leading-relaxed">{pick(b)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section — Which solution is right for you? */}
      <section className="py-16 lg:py-20 bg-white" data-testid="pricing-chooser">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'ENCUENTRA TU PLATAFORMA' : 'FIND YOUR PLATFORM'}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight">
              {isEs ? '¿Qué solución es la correcta para ti?' : 'Which solution is right for you?'}
            </h2>
          </div>
          <div className="rounded-2xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden">
            {CHOOSER.map((row, i) => (
              <Link
                key={i}
                to={row.href}
                className="group flex items-center justify-between gap-4 px-5 sm:px-7 py-4 bg-white hover:bg-orange-50/50 transition-colors"
                data-testid={`chooser-row-${i}`}
              >
                <p className="text-[14.5px] text-neutral-700 italic min-w-0">{pick(row.need)}</p>
                <span className="inline-flex items-center gap-1.5 font-semibold text-[14px] text-neutral-900 group-hover:text-[#F77F11] transition-colors shrink-0 whitespace-nowrap">
                  {typeof row.name === 'string' ? row.name : pick(row.name)}
                  <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-[#F77F11] group-hover:translate-x-0.5 transition-all" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section — How we work */}
      <HowWeWork />

      {/* Section — Managed Growth Programs */}
      <section className="py-16 lg:py-20 bg-white" data-testid="pricing-addons">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'SERVICIOS DE CRECIMIENTO' : 'GROWTH SERVICES'}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Managed Growth Programs' : 'Managed Growth Programs'}
            </h2>
            <p className="mt-3 text-neutral-600 text-[15px]">
              {isEs
                ? 'No vendemos tarifas — gestionamos transformaciones. Cada programa complementa tus plataformas.'
                : 'We don’t sell tariffs — we manage transformations. Each program complements your platforms.'}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map(({ icon: Icon, name, href, headline, para, caps, price }) => (
              <div key={href} className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-7 hover:border-[#F77F11]/60 hover:shadow-[0_30px_70px_-40px_rgba(247,127,17,0.4)] transition-all" data-testid={`module-${href.split('?')[0].split('/').pop()}`}>
                <div className="flex items-center justify-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#F77F11]" />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400 min-w-0">{pick(name)}</p>
                </div>
                <h3 className="mt-5 text-center text-[21px] font-bold text-neutral-900 tracking-[-0.01em] leading-snug">{pick(headline)}</h3>
                <p className="mt-2.5 text-center text-[13.5px] text-neutral-600 leading-relaxed">{pick(para)}</p>
                <ul className="mt-4 space-y-1.5">
                  {pick(caps).map((x) => (
                    <li key={x} className="flex items-center gap-2 text-[13.5px] text-neutral-700">
                      <Check className="w-4 h-4 text-[#F77F11] shrink-0" /> <span className="min-w-0">{x}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-neutral-900 text-[14px] whitespace-nowrap">
                      {price
                        ? (isEs ? `Desde $${price}/mes` : `From $${price}/mo`)
                        : (isEs ? 'Cotización a medida' : 'Custom Quote')}
                    </p>
                    <Link to={href} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 hover:border-[#F77F11] hover:text-[#F77F11] text-neutral-800 px-5 h-10 text-[13.5px] font-semibold transition-colors shrink-0" data-testid={`module-explore-${href.split('?')[0].split('/').pop()}`}>
                      {isEs ? 'Explorar' : 'Explore'} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <p className="mt-2.5 text-[11.5px] text-neutral-400 leading-relaxed">{pick(SCOPE_NOTE)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section — FAQ */}
      <section className="py-16 lg:py-20 bg-neutral-50 border-y border-neutral-200" data-testid="pricing-faq">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="kx-eyebrow mb-4">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Preguntas frecuentes' : 'Frequently asked questions'}
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-neutral-200 bg-white px-6 py-5" data-testid={`pricing-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold text-[15px] text-neutral-900">
                  {pick(f.q)}
                  <span className="text-[#F77F11] transition-transform group-open:rotate-45 shrink-0 text-lg leading-none">+</span>
                </summary>
                <p className="mt-3 text-[14.5px] text-neutral-600 leading-relaxed">{pick(f.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Enterprise */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-white to-orange-50/50" data-testid="pricing-enterprise">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight">
            {isEs ? '¿Necesitas algo diferente?' : 'Need something different?'}
          </h2>
          <p className="mt-3 text-neutral-600">
            {isEs
              ? 'Soluciones Enterprise a medida para grupos, cadenas y operaciones complejas.'
              : 'Custom Enterprise solutions for groups, chains and complex operations.'}
          </p>
          <Button asChild className="mt-7 bg-neutral-950 hover:bg-neutral-800 text-white h-12 px-8 rounded-full font-semibold">
            <Link to="/contact?type=discovery" data-testid="pricing-enterprise-cta">
              {isEs ? 'Agendar Discovery Call' : 'Book Discovery Call'} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </section>

      <ScanPromo className="pb-6" />

      <ShareEarnBar />

      <AffiliateStrip className="pt-10 pb-20" />
    </div>
  );
}
